import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  ChevronLeft,
  Calendar,
  Users,
  Clock,
  MapPin,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

const Button = /** @type {React.ComponentType<any>} */ (RawButton);
const Card = /** @type {React.ComponentType<any>} */ (RawCard);

/**
 * @typedef {Object} Course
 * @property {string} image
 * @property {string} name
 * @property {string} city
 * @property {string} country
 * @property {number} price
 *
 * @typedef {Object} BookingData
 * @property {Date} date
 * @property {string} teeTime
 * @property {number} players
 *
 * @typedef {Object} CheckoutViewProps
 * @property {Course} course
 * @property {BookingData} bookingData
 * @property {() => void} onBack
 * @property {(details: { course: Course, date: Date, teeTime: string, players: number, totalAmount: number }) => void} onConfirm
 * @property {() => void} [onReturnToMenu]
 */

/**
 * @param {CheckoutViewProps} props
 */
export default function CheckoutView({
  course,
  bookingData,
  onBack,
  onConfirm,
  onReturnToMenu,
}) {
  const totalAmount = course.price * bookingData.players;

  const handleConfirm = () => {
    onConfirm({
      course,
      date: bookingData.date,
      teeTime: bookingData.teeTime,
      players: bookingData.players,
      totalAmount,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Course
          </Button>
          {onReturnToMenu && (
            <Button
              variant="outline"
              onClick={onReturnToMenu}
              className="gap-2 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white hover:text-white border-none"
            >
              Return to Booking Menu
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold mb-2 text-[#0B2421]">
            Confirm Golf Selection
          </h1>
          <p className="mb-8 text-gray-500">
            Review your booking details
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="p-8 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold mb-6 text-[#0B2421]">
                Booking Summary
              </h2>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={course.image}
                      alt={course.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0B2421]">
                      {course.name}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                      <MapPin className="h-3 w-3" />
                      <span>{course.city}, {course.country}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium ml-auto text-[#0B2421]">
                      {format(bookingData.date, "dd MMM yyyy")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Tee Time:</span>
                    <span className="font-medium ml-auto text-[#0B2421]">
                      {bookingData.teeTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Players:</span>
                    <span className="font-medium ml-auto text-[#0B2421]">
                      {bookingData.players}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Price per player</span>
                    <span className="font-medium text-[#0B2421]">SGD {course.price}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Number of players</span>
                    <span className="font-medium text-[#0B2421]">× {bookingData.players}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200">
                    <span className="text-[#0B2421]">Subtotal</span>
                    <span className="text-[#D4AF37]">SGD {totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-700">
                  💡 Payment will be processed after you complete selecting all services
                </p>
              </div>

              <Button
                onClick={handleConfirm}
                className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Confirm Golf Selection
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
